import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react';
import { cn } from './utils/cn';

export type ToastTone = 'success' | 'info' | 'warning' | 'danger';

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** Auto-dismiss delay in ms. 0 disables auto-dismiss. Default: 4000. */
  duration?: number;
}

interface ToastItem extends Required<Pick<ToastInput, 'title' | 'tone' | 'duration'>> {
  id: number;
  description?: ReactNode;
}

interface ToastCtx {
  toast: (input: ToastInput) => number;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function useToast(): ToastCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast must be used inside <ToastProvider>');
  return c;
}

export interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((s) => s.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((input: ToastInput) => {
    idRef.current += 1;
    const id = idRef.current;
    const item: ToastItem = {
      id,
      title: input.title,
      ...(input.description !== undefined ? { description: input.description } : {}),
      tone: input.tone ?? 'info',
      duration: input.duration ?? 4000,
    };
    setItems((s) => [...s, item]);
    if (item.duration > 0) {
      window.setTimeout(() => dismiss(id), item.duration);
    }
    return id;
  }, [dismiss]);

  const value = useMemo<ToastCtx>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <ToastViewport items={items} dismiss={dismiss} />
    </Ctx.Provider>
  );
}

function ToastViewport({ items, dismiss }: { items: ToastItem[]; dismiss: (id: number) => void }) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed bottom-4 end-4 z-[60] flex flex-col gap-2 max-w-sm pointer-events-none"
    >
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

const TONE_STYLES: Record<ToastTone, { ring: string; icon: ReactNode }> = {
  success: { ring: 'border-success/40', icon: <CheckCircle2 className="h-5 w-5 text-success" /> },
  info:    { ring: 'border-info/40',    icon: <Info className="h-5 w-5 text-info" /> },
  warning: { ring: 'border-warning/40', icon: <TriangleAlert className="h-5 w-5 text-warning" /> },
  danger:  { ring: 'border-danger/40',  icon: <XCircle className="h-5 w-5 text-danger" /> },
};

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const { ring, icon } = TONE_STYLES[item.tone];
  // Animate in on mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setMounted(true), 10);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto bg-surface border rounded-xl shadow-lg p-4 min-w-[260px]',
        'flex items-start gap-3',
        'transition-all duration-200',
        mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
        ring,
      )}
    >
      <span className="shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-tight">{item.title}</div>
        {item.description ? (
          <div className="text-xs text-fg-muted mt-1">{item.description}</div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-fg-subtle hover:text-fg p-1 -m-1 rounded-md"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
